import { TimeoutError } from './withTimeout';

/**
 * Firebase auth error codes mapped to messages we are willing to show a user.
 *
 * Sign-in failures are deliberately collapsed into one message. Firebase
 * returns auth/invalid-credential rather than distinguishing "no such user"
 * from "wrong password" so that the form cannot be used to discover which
 * email addresses have accounts, and telling them apart here would hand that
 * back.
 */
const SIGN_IN_FAILED =
  'Nieprawidłowy e-mail lub hasło. Sprawdź dane i spróbuj ponownie.';

const MESSAGES: Record<string, string> = {
  // Sign in
  'auth/invalid-credential': SIGN_IN_FAILED,
  'auth/wrong-password': SIGN_IN_FAILED,
  'auth/user-not-found': SIGN_IN_FAILED,
  'auth/invalid-login-credentials': SIGN_IN_FAILED,

  // Sign up
  'auth/email-already-in-use': 'Ten adres e-mail jest już zarejestrowany.',
  'auth/weak-password':
    'Hasło jest zbyt słabe. Użyj co najmniej 6 znaków, najlepiej z cyfrą.',

  // Google sign-in popup. Closed/cancelled popups are deliberately absent —
  // callers treat them as a change of mind and show nothing.
  'auth/popup-blocked':
    'Przeglądarka zablokowała okno logowania. Zezwól na wyskakujące okienka i spróbuj ponownie.',
  'auth/unauthorized-domain':
    'Logowanie przez Google jest chwilowo niedostępne. Spróbuj ponownie później.',
  'auth/account-exists-with-different-credential':
    'Konto z tym adresem e-mail już istnieje i używa innej metody logowania.',

  // Shared
  'auth/invalid-email': 'To nie wygląda na poprawny adres e-mail.',
  'auth/user-disabled':
    'To konto zostało zablokowane. Skontaktuj się z nami, aby je odblokować.',
  'auth/too-many-requests':
    'Zbyt wiele prób logowania. Odczekaj kilka minut i spróbuj ponownie.',
  'auth/network-request-failed':
    'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.',
  // Provider-neutral: this is also what the Google popup rejects with when
  // the provider is not enabled in the Firebase console.
  'auth/operation-not-allowed':
    'Ta metoda logowania jest wyłączona. Skontaktuj się z nami.',
};

/**
 * Turns an auth failure into something worth showing a user.
 *
 * Firebase error messages read like "Firebase: Error (auth/invalid-credential)."
 * — the code is the useful part and the rest is noise, so map the code and fall
 * back to a plain sentence for anything unrecognised rather than leaking the
 * raw SDK string.
 */
export function getAuthErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof TimeoutError) {
    return err.message;
  }

  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = String((err as { code: unknown }).code);
    if (MESSAGES[code]) {
      return MESSAGES[code];
    }
  }

  return fallback;
}
